/**
 * Shared data source for Research, Stories, Media, and Community updates
 * Used by both the homepage section (EBooksSection2) and the full updates page
 *
 * Every JSON file in src/content/updates is one update card. The files are
 * written by the CMS (see public/admin/config.yml and docs/CMS.md), so the
 * field names there are the contract; this module only loads, sorts and
 * derives a couple of presentation values.
 */

/**
 * @typedef {Object} Update
 * @property {string} slug - File name without extension; stable key
 * @property {string} title - Card title
 * @property {string} category - Research, Stories, Media or Community
 * @property {string} subtitle - Card body text
 * @property {string} status - Button label (eBook, Case Study, Shared, ...)
 * @property {string} link - Internal path or full URL
 * @property {'internal'|'external'} linkType - Derived from the link
 * @property {string} [image] - Optional card image
 * @property {string} [imageAlt] - Alt text for the image
 * @property {string} date - YYYY-MM-DD, newest first
 * @property {boolean} featured - Shown on the homepage
 * @property {boolean} available - False renders a dimmed "coming soon" card
 * @property {boolean} [draft] - True hides the card everywhere
 */

/**
 * Load every update file, newest first.
 *
 * `require.context` is webpack-only. Under Jest there is no webpack, so the
 * list is empty there. (Don't "feature-detect" it: any bare reference to
 * `require` makes webpack bundle the whole directory as a context module.)
 *
 * @returns {Update[]} Sorted, non-draft updates
 */
function loadUpdates() {
  if (process.env.NODE_ENV === 'test') {
    return [];
  }

  const context = require.context('../content/updates', false, /\.json$/);

  return (
    context
      .keys()
      // webpack can list the same file under more than one key; keep the
      // relative form only.
      .filter((key) => key.startsWith('./'))
      .map((key) => {
        const entry = context(key);
        return {
          ...entry,
          slug: key.replace(/^\.\//, '').replace(/\.json$/, ''),
          linkType: /^https?:\/\//i.test(entry.link || '')
            ? 'external'
            : 'internal',
        };
      })
      .filter((update) => !update.draft)
      // Newest first; same-day items fall back to file name so the order is
      // stable between builds.
      .sort(
        (a, b) =>
          (b.date || '').localeCompare(a.date || '') ||
          a.slug.localeCompare(b.slug)
      )
  );
}

/** @type {Update[]} */
export const updatesData = loadUpdates();

/**
 * Get featured updates for the homepage
 * @returns {Array} Array of featured update items
 */
export const getFeaturedUpdates = () => {
  return updatesData.filter((update) => update.featured).slice(0, 12);
};

/**
 * Get all updates
 * @returns {Array} Array of all update items
 */
export const getAllUpdates = () => {
  return updatesData;
};

/**
 * Get updates by category
 * @param {string} category - Category to filter by
 * @returns {Array} Array of updates matching the category
 */
export const getUpdatesByCategory = (category) => {
  return updatesData.filter((update) => update.category === category);
};

/**
 * Get available categories in the specified order
 * @returns {Array} Array of unique category names in display order
 */
export const getCategories = () => {
  // Define the desired display order
  const displayOrder = ['Research', 'Stories', 'Media', 'Community'];

  // Get unique categories from data
  const dataCategories = [
    ...new Set(updatesData.map((update) => update.category)),
  ];

  // Return categories in the specified order, plus any new ones at the end
  const orderedCategories = displayOrder.filter((cat) =>
    dataCategories.includes(cat)
  );
  const extraCategories = dataCategories.filter(
    (cat) => !displayOrder.includes(cat)
  );

  return [...orderedCategories, ...extraCategories];
};
